<script setup lang="ts">
import { captureException } from '@sentry/vue';
import { ElButton, ElIcon } from 'element-plus';
import { computed, ref, useAttrs } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import { RouterLink } from 'vue-router';
import FaIcon from './FaIcon.vue';

interface Props {
  /** Instead of `@click`. Shows a spinner and blocks further clicks until the promise settles. */
  handler?: () => unknown;
  /** Renders as a `RouterLink`. */
  to?: RouteLocationRaw;
  /** Renders as an `<a>`. */
  href?: string;
  /** Opens `href` in a new tab. */
  external?: boolean;
}

const props = defineProps<Props>();

defineOptions({ inheritAttrs: false });

// `size="default"` sits before `v-bind` on purpose: below the md breakpoint,
// `provideGlobalConfig` raises the global size to `large` (52px) so fields grow, but CTAs
// should stay 44px everywhere — and the call site still has the last word, since $attrs
// comes after and overrides it. (A template comment here would add a second root node.)

const attrs = useAttrs();
const running = ref(false);

const tag = computed(() => {
  if (props.to !== undefined) return RouterLink;
  if (props.href !== undefined) return 'a';

  return 'button';
});

const linkAttrs = computed(() => {
  if (props.to !== undefined) return { to: props.to };
  if (props.href === undefined) return {};

  return props.external
    ? { href: props.href, target: '_blank', rel: 'noreferrer noopener' }
    : { href: props.href };
});

// `loading` stays settable from outside, even without `handler`.
const loading = computed(() => running.value || attrs.loading === true || attrs.loading === '');

async function onClick(event: MouseEvent): Promise<void> {
  // Element Plus only blocks once it has seen `loading` as a prop — a second click in the
  // same tick still arrives before the re-render.
  if (running.value) {
    event.stopPropagation();

    return;
  }

  if (props.handler === undefined) return;

  running.value = true;

  try {
    await props.handler();
  } catch (error) {
    // Without this, a rejection ends up as an unhandled rejection with no useful stack.
    captureException(error);
  } finally {
    running.value = false;
  }
}
</script>

<template>
  <ElButton
    size="default"
    v-bind="{ ...$attrs, ...linkAttrs }"
    :tag="tag"
    :loading="loading"
    @click="onClick"
  >
    <template v-if="$slots.icon" #icon>
      <slot name="icon" />
    </template>
    <template #loading>
      <slot name="loading">
        <!-- ElIcon only for the text spacing Element Plus sets via `[class*=el-icon]+span`. -->
        <ElIcon class="app-button__spinner">
          <FaIcon name="spinner-third" variant="duotone" spin />
        </ElIcon>
      </slot>
    </template>
    <template v-if="$slots.default" #default>
      <slot />
    </template>
  </ElButton>
</template>

<style scoped>
/* Not the color — both duotone layers are `fill: currentColor` already. This is how much of
   it the track shows: FA's 0.4 reads washed on a filled button next to the opaque arc. Not
   1, or track and arc merge into one ring and the rotation stops being visible. */
.app-button__spinner {
  --fa-secondary-opacity: 0.65;
  --color: currentColor;
}
</style>
